import "./style.scss";

import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import SelectFormComponent from "components/shared/FormComponent/SelectFormComponent";
import { formatLocaleDateString } from "helpers/utils";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { ITransferTermFormData } from "..";
import { getClosestDate, getMonthsBetweenDates } from "../utils";

interface IDatesStep {
    indexStep: number;
    stepTitle: string;
    stepSubtitle: string;
    useFormData: Omit<ITransferTermFormData, "isDirty">;
    mode: string;
}

/**
 * Third step of TransferTermForm to select dates and frequence of payment.
 */
const DateStep = ({ indexStep, stepSubtitle, stepTitle, useFormData, mode }: IDatesStep) => {
    const [t] = useTranslation();
    const { control, errors, watch, setValue } = useFormData;
    const startDate = watch("startDate");
    const endDate = watch("expirationDate");
    const periodDuration = watch("periodDuration");

    const paymentDateOptions = useMemo(() => {
        if (startDate && endDate && periodDuration) {
            const numberOfPayments =
                Math.floor(getMonthsBetweenDates(new Date(startDate), new Date(endDate))) / periodDuration + 1;

            const salesDate = Array.from({ length: numberOfPayments }, (_, i) => {
                const dueDate = new Date(startDate);
                dueDate.setUTCMonth(dueDate.getUTCMonth() + i * periodDuration);
                return {
                    value: dueDate.getTime(),
                    label: `Paiement du ${formatLocaleDateString({
                        date: dueDate,
                        options: { numeric: true, timeZone: "UTC" },
                    })}`,
                };
            });
            return salesDate ?? [];
        } else {
            return [];
        }
    }, [startDate, endDate, periodDuration]);

    const closestDate = useMemo(() => {
        if (paymentDateOptions.length === 0) return null;
        return getClosestDate(paymentDateOptions, new Date());
    }, [paymentDateOptions]);

    const renderOption = (option: { value: number; label: string }) => {
        return (
            <div className="upcoming-date-option">
                <span>
                    {t("TransferTerms.TermForm.PaymentOption", {
                        date: formatLocaleDateString({
                            date: new Date(option.value),
                            options: { numeric: true, timeZone: "UTC" },
                        }),
                    })}
                </span>
                {closestDate?.value === option.value && (
                    <div className="upcoming-date-chip">{t("TransferTerms.TermForm.ClosestDate")}</div>
                )}
            </div>
        );
    };

    useEffect(() => {
        if (closestDate) {
            setValue("upcomingPaymentDate", closestDate.value);
        }
    }, [closestDate, setValue]);

    return (
        <>
            <h3 className="form-section-title first">
                {" "}
                {indexStep + 1}. {stepTitle}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="form-fields">
                <NumberFormComponent
                    control={control}
                    controlName="periodDuration"
                    label={t("TransferTerms.TermForm.PaymentFrequence")}
                    error={errors.periodDuration}
                    required
                    icon={<span>{t("TransferTerms.TermForm.Months")}</span>}
                />
                <NumberFormComponent
                    control={control}
                    controlName="paymentDelay"
                    label={t("TransferTerms.TermForm.PaymentDelay")}
                    error={errors.paymentDelay}
                    required
                    icon={<span>{t("TransferTerms.TermForm.Days")}</span>}
                />

                {mode === "new" && (
                    <>
                        <SelectFormComponent
                            label={t("TransferTerms.TermForm.UpcomingDate")}
                            control={control}
                            controlName="upcomingPaymentDate"
                            error={errors.upcomingPaymentDate}
                            items={paymentDateOptions}
                            renderItem={renderOption}
                            disabled={!startDate || !endDate || !periodDuration}
                            infoTooltip={
                                !startDate || !endDate || !periodDuration
                                    ? { text: t("TransferTerms.TermForm.UpcomingDateTooltip"), type: "info" }
                                    : undefined
                            }
                        />
                        <span className="sales-info">{t("TransferTerms.TermForm.SalesInfo")}</span>
                    </>
                )}
            </div>
        </>
    );
};

export default DateStep;
